/**
 * Hours → FTEs.
 *
 * The formula is the prototype's, unchanged:
 *   FTEs = accounts × hoursPerAccountPerMonth × 12 / hrsPerFteYear
 *
 * `hrsPerFteYear` is 2000 in the current config. Note what that number is NOT:
 * it is a nominal working year, while the companion research brief benchmarks
 * real CSM capacity at ~100–140 PRODUCTIVE hours/month (≈1200–1680/year). So an
 * FTE figure computed here is optimistic relative to the productive line, which
 * matters because the Small tier already sits near it. Callers surfacing a
 * capacity warning should compare against the productive benchmark, not against
 * 1.0 FTE.
 */

import type { Account, Portfolio, StaffingConfig, TierDef } from './types.ts';
import { portfolioAccounts } from './assign.ts';

/**
 * Validate the arithmetic inputs these helpers cannot recover from.
 *
 * `computePortfolios` validates its own path, but every function in this file is
 * a public export callable on its own — and the failure mode is not a crash, it
 * is a plausible-looking number. `NaN` hours or a `NaN` count propagate straight
 * into `monthlyHours` and `ftesNeeded` and render as a staffing figure; a
 * negative count yields negative FTE demand, which reads as spare capacity.
 * Cheaper to refuse than to explain later why a plan was built on it.
 */
function assertCountAndTier(accountCount: number, tier: TierDef): void {
  if (!Number.isFinite(accountCount) || accountCount < 0) {
    throw new Error(
      `staffing: accountCount must be a finite, non-negative number (got ${accountCount})`,
    );
  }
  if (
    !Number.isFinite(tier.hoursPerAccountPerMonth) ||
    tier.hoursPerAccountPerMonth < 0
  ) {
    throw new Error(
      `staffing: tier '${tier.key}' has invalid hoursPerAccountPerMonth (${tier.hoursPerAccountPerMonth})`,
    );
  }
}

/** Monthly service hours implied by a set of accounts, all in one tier. */
export function monthlyHours(accountCount: number, tier: TierDef): number {
  assertCountAndTier(accountCount, tier);
  return accountCount * tier.hoursPerAccountPerMonth;
}

/** FTEs needed to serve `accountCount` accounts at this tier's service level. */
export function ftesNeeded(
  accountCount: number,
  tier: TierDef,
  hrsPerFteYear: number,
): number {
  assertCountAndTier(accountCount, tier);
  // `Number.isFinite` first: `NaN <= 0` is FALSE, so the original guard let NaN
  // straight through and returned NaN FTEs.
  if (!Number.isFinite(hrsPerFteYear) || hrsPerFteYear <= 0) {
    throw new Error(
      `staffing: hrsPerFteYear must be a positive, finite number (got ${hrsPerFteYear})`,
    );
  }
  return (accountCount * tier.hoursPerAccountPerMonth * 12) / hrsPerFteYear;
}

export interface TierLoad {
  tier: string;
  accounts: number;
  bookingVolumeM: number;
  monthlyHours: number;
  ftesNeeded: number;
}

/** Per-tier load across the whole book. */
export function tierLoads(
  byTier: Readonly<Record<string, Account[]>>,
  config: StaffingConfig,
): TierLoad[] {
  return config.tiers.map((tier) => {
    const accounts = byTier[tier.key] ?? [];
    return {
      tier: tier.key,
      accounts: accounts.length,
      bookingVolumeM: accounts.reduce((sum, a) => sum + a.spendM, 0),
      monthlyHours: monthlyHours(accounts.length, tier),
      ftesNeeded: ftesNeeded(accounts.length, tier, config.hrsPerFteYear),
    };
  });
}

export interface PortfolioLoad {
  csm: string;
  tier: string | null;
  accounts: number;
  bookingVolumeM: number;
  monthlyHours: number;
  ftesNeeded: number;
}

/**
 * One CSM's load.
 *
 * Hours are charged at the tier of EACH account, not the CSM's own tier — an
 * enterprise CSM holding a pinned small-tier account should be charged the small
 * tier's 0.5 hrs for it, not enterprise's 15. The prototype had no need to get
 * this right because it never totalled hours per CSM.
 */
export function portfolioLoad(
  portfolio: Portfolio,
  tierOfAccount: ReadonlyMap<string, string>,
  config: StaffingConfig,
): PortfolioLoad {
  const byKey = new Map(config.tiers.map((t) => [t.key, t]));
  const accounts = portfolioAccounts(portfolio);

  let hours = 0;
  let ftes = 0;
  for (const account of accounts) {
    const key = tierOfAccount.get(account.id);
    const tier = key === undefined ? undefined : byKey.get(key);
    if (tier === undefined) continue;
    hours += tier.hoursPerAccountPerMonth;
    ftes += ftesNeeded(1, tier, config.hrsPerFteYear);
  }

  return {
    csm: portfolio.csm,
    tier: portfolio.tier,
    accounts: accounts.length,
    bookingVolumeM: accounts.reduce((sum, a) => sum + a.spendM, 0),
    monthlyHours: hours,
    ftesNeeded: ftes,
  };
}
